import Dexie, { type Table } from "dexie";
import {
  bumpSyncMeta,
  estimateJsonInputSize,
  measurePerformanceAsync,
  tombstone,
  type Asset,
  type AssetBlob,
  type Character,
  type Chat,
  type GameCard,
  type GeneratedImage,
  type InstalledRqtPack,
  type Lorebook,
  type LiveQuery,
  type Message,
  type MessagePage,
  type Persona,
  type Preset,
  type ProviderProfile,
  type Repository,
  type Script,
  type Settings,
  type StorageProvider,
} from "@roqeet/core";
import { getDB, type RoqeetDB } from "./schema.js";
import { DexieRepository } from "./repository.js";
import { toLiveQuery } from "./live-query.js";
import { getDeviceOrigin } from "./origin.js";
import {
  normalizeStoredGameCard,
  storedGameCardSortKey,
  type StoredGameCardNormalizationResult,
} from "./stored-game-card.js";

// Dexie key-range sentinels for compound-index cursor paging (decision #9).
const MIN_KEY = Dexie.minKey;
const MAX_KEY = Dexie.maxKey;
const loggedStoredGameCardIssues = new Set<string>();
const BACKUP_IMPORT_TABLES = [
  "characters",
  "personas",
  "chats",
  "lorebooks",
  "presets",
  "scripts",
  "providerProfiles",
  "generatedImages",
  "assets",
  "messages",
  "settings",
  "gameCards",
  "installedRqtPacks",
] as const;
const FORBIDDEN_BACKUP_DATA_KEYS = new Set(["assetBlobs", "keyVault", "providerVault", "secrets", "vault"]);
const UNSAFE_OBJECT_KEYS = new Set(["__proto__", "constructor", "prototype"]);

type BackupImportTable = (typeof BACKUP_IMPORT_TABLES)[number];
type ValidatedBackupImportData = Partial<Record<BackupImportTable, unknown[]>>;

class DexieGameCardRepository implements Repository<GameCard> {
  constructor(private readonly table: Table<GameCard, string>) {}

  async get(id: string): Promise<GameCard | undefined> {
    const raw = await this.table.get(id);
    if (raw === undefined) return undefined;
    const result = normalizeStoredGameCard(raw);
    const card = await this.cardFromResult(result, { repair: true });
    return card && !card.sync.deletedAt ? card : undefined;
  }

  async getMany(ids: string[]): Promise<(GameCard | undefined)[]> {
    const values = await this.table.bulkGet(ids);
    return Promise.all(
      values.map(async (value) => {
        if (value === undefined) return undefined;
        const result = normalizeStoredGameCard(value);
        const card = await this.cardFromResult(result, { repair: true });
        return card && !card.sync.deletedAt ? card : undefined;
      }),
    );
  }

  async list(): Promise<GameCard[]> {
    const rows = await this.table.toArray();
    return this.normalizeRows(rows, { repair: true });
  }

  async put(value: GameCard): Promise<void> {
    await measurePerformanceAsync(
      "save_project_state",
      {
        inputSize: estimateJsonInputSize(value),
        fileId: this.table.name,
        packageId: value.id,
        cache: "miss",
        thread: "main",
        invalidationReason: "entity_put",
      },
      async () => {
        const origin = getDeviceOrigin();
        const next = { ...value, sync: await bumpSyncMeta(value, origin) };
        await this.table.put(next);
      },
    );
  }

  async putMany(values: GameCard[]): Promise<void> {
    await measurePerformanceAsync(
      "save_project_state",
      {
        inputSize: estimateJsonInputSize(values),
        fileId: this.table.name,
        packageId: values.map((v) => v.id).join(","),
        cache: "miss",
        thread: "main",
        invalidationReason: "entity_bulk_put",
        detail: { count: values.length },
      },
      async () => {
        const origin = getDeviceOrigin();
        const next = await Promise.all(
          values.map(async (v) => ({ ...v, sync: await bumpSyncMeta(v, origin) })),
        );
        await this.table.bulkPut(next);
      },
    );
  }

  async delete(id: string): Promise<void> {
    const raw = await this.table.get(id);
    if (raw === undefined) return;
    const result = normalizeStoredGameCard(raw);
    const card = await this.cardFromResult(result, { repair: false });
    if (!card) return;
    await this.table.put({
      ...card,
      sync: tombstone(card.sync, getDeviceOrigin()),
    });
  }

  async count(): Promise<number> {
    return (await this.list()).length;
  }

  async repairAll(): Promise<void> {
    await this.list();
  }

  async normalizeRows(rows: unknown[], options: { repair: boolean }): Promise<GameCard[]> {
    const cards: GameCard[] = [];
    for (const raw of rows) {
      const result = normalizeStoredGameCard(raw);
      const card = await this.cardFromResult(result, options);
      if (card && !card.sync.deletedAt) cards.push(card);
    }
    return cards.sort((a, b) => storedGameCardSortKey(a).localeCompare(storedGameCardSortKey(b)));
  }

  private async cardFromResult(
    result: StoredGameCardNormalizationResult,
    options: { repair: boolean },
  ): Promise<GameCard | undefined> {
    logStoredGameCardNormalization(result);
    if (result.status !== "ok") return undefined;
    if (!result.repaired || !options.repair) return result.card;

    const repaired = {
      ...result.card,
      sync: await bumpSyncMeta(result.card, getDeviceOrigin()),
    };
    await this.table.put(repaired);
    return repaired;
  }
}

function logStoredGameCardNormalization(result: StoredGameCardNormalizationResult): void {
  if (result.status === "invalid") {
    const key = `invalid:${result.rawId ?? "unknown"}:${result.reason}`;
    if (loggedStoredGameCardIssues.has(key)) return;
    loggedStoredGameCardIssues.add(key);
    console.warn(
      `[storage] Skipping invalid stored GameCard${result.rawId ? ` (${result.rawId})` : ""}: ${result.reason}`,
      result.warnings,
    );
    return;
  }

  if (!result.repaired) return;
  const key = `repaired:${result.card.id}:${result.warnings.join("|")}`;
  if (loggedStoredGameCardIssues.has(key)) return;
  loggedStoredGameCardIssues.add(key);
  console.warn(`[storage] Repaired stored GameCard (${result.card.id})`, result.warnings);
}

function validateBackupImportData(data: unknown): ValidatedBackupImportData {
  if (!isRecord(data)) {
    throw new Error("Backup data must be an object.");
  }

  for (const key of Object.keys(data)) {
    if (FORBIDDEN_BACKUP_DATA_KEYS.has(key)) {
      throw new Error(`Backup data cannot restore raw "${key}" records.`);
    }
  }
  if (data["version"] !== undefined && typeof data["version"] !== "number") {
    throw new Error("Backup data version must be a number.");
  }

  const validated: ValidatedBackupImportData = {};
  for (const table of BACKUP_IMPORT_TABLES) {
    const rows = data[table];
    if (rows === undefined) continue;
    if (!Array.isArray(rows)) {
      throw new Error(`Backup table "${table}" must be an array.`);
    }
    validated[table] = table === "gameCards"
      ? normalizeBackupGameCards(rows)
      : validateBackupTableRows(table, rows);
  }
  return validated;
}

export function validateBackupImportDataForRestore(data: unknown): void {
  validateBackupImportData(data);
}

function normalizeBackupGameCards(rows: unknown[]): GameCard[] {
  return rows.map((row, index) => {
    const result = normalizeStoredGameCard(row);
    logStoredGameCardNormalization(result);
    if (result.status !== "ok") {
      throw new Error(`Backup gameCards[${index}] is invalid: ${result.reason}`);
    }
    return result.card;
  });
}

function validateBackupTableRows(table: BackupImportTable, rows: unknown[]): unknown[] {
  return rows.map((row, index) => {
    if (!isRecord(row)) {
      throw new Error(`Backup ${table}[${index}] must be an object.`);
    }
    validateNoUnsafeObjectKeys(row, `Backup ${table}[${index}]`);
    return row;
  });
}

function validateNoUnsafeObjectKeys(value: unknown, path: string, seen = new WeakSet<object>()): void {
  if (!value || typeof value !== "object") return;
  if (seen.has(value)) return;
  seen.add(value);

  if (Array.isArray(value)) {
    value.forEach((item, index) => validateNoUnsafeObjectKeys(item, `${path}[${index}]`, seen));
    return;
  }

  for (const [key, child] of Object.entries(value)) {
    if (UNSAFE_OBJECT_KEYS.has(key)) {
      throw new Error(`${path} contains unsafe key "${key}".`);
    }
    validateNoUnsafeObjectKeys(child, `${path}.${key}`, seen);
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

/**
 * Web implementation of the core {@link StorageProvider} port (decision #3),
 * backed by Dexie/IndexedDB. This is the single place the app touches
 * IndexedDB; the platform-neutral core only ever sees the interface
 * (decision #10), so a React Native app can later swap in a SQLite-backed
 * implementation of the same contract.
 */
export class DexieStorageProvider implements StorageProvider {
  private readonly db: RoqeetDB = getDB();
  private readonly gameCardRepository: DexieGameCardRepository;

  readonly characters: Repository<Character>;
  readonly personas: Repository<Persona>;
  readonly chats: Repository<Chat>;
  readonly lorebooks: Repository<Lorebook>;
  readonly presets: Repository<Preset>;
  readonly scripts: Repository<Script>;
  readonly providerProfiles: Repository<ProviderProfile>;
  readonly generatedImages: Repository<GeneratedImage>;
  readonly assets: Repository<Asset>;
  readonly gameCards: Repository<GameCard>;
  readonly installedRqtPacks: Repository<InstalledRqtPack>;

  constructor() {
    this.characters = new DexieRepository(this.db.characters);
    this.personas = new DexieRepository(this.db.personas);
    this.chats = new DexieRepository(this.db.chats);
    this.lorebooks = new DexieRepository(this.db.lorebooks);
    this.presets = new DexieRepository(this.db.presets);
    this.scripts = new DexieRepository(this.db.scripts);
    this.providerProfiles = new DexieRepository(this.db.providerProfiles);
    this.generatedImages = new DexieRepository(this.db.generatedImages);
    this.assets = new DexieRepository(this.db.assets);
    this.gameCardRepository = new DexieGameCardRepository(this.db.gameCards);
    this.gameCards = this.gameCardRepository;
    this.installedRqtPacks = new DexieRepository(this.db.installedRqtPacks);
  }

  async open(): Promise<void> {
    await measurePerformanceAsync(
      "open_project",
      {
        inputSize: 0,
        fileId: "indexeddb://roqeet-rp",
        packageId: "local-project",
        cache: "unknown",
        thread: "main",
        invalidationReason: "app_boot",
      },
      () => this.db.open(),
    );
    await this.gameCardRepository.repairAll();
  }

  async close(): Promise<void> {
    this.db.close();
  }

  // ---- Messages: paged, never blob-loaded (decision #9) ----

  async getMessage(id: string): Promise<Message | undefined> {
    const m = await this.db.messages.get(id);
    return m && !m.sync.deletedAt ? m : undefined;
  }

  async putMessage(message: Message): Promise<void> {
    await measurePerformanceAsync(
      "save_project_state",
      {
        inputSize: estimateJsonInputSize(message),
        fileId: "messages",
        packageId: message.chatId,
        cache: "miss",
        thread: "main",
        invalidationReason: "message_put",
      },
      async () => {
        const next: Message = { ...message, sync: await bumpSyncMeta(message, getDeviceOrigin()) };
        await this.db.messages.put(next);
      },
    );
  }

  async putMessages(messages: Message[]): Promise<void> {
    await measurePerformanceAsync(
      "save_project_state",
      {
        inputSize: estimateJsonInputSize(messages),
        fileId: "messages",
        packageId: messages[0]?.chatId ?? "",
        cache: "miss",
        thread: "main",
        invalidationReason: "message_bulk_put",
        detail: { count: messages.length },
      },
      async () => {
        const origin = getDeviceOrigin();
        const next = await Promise.all(
          messages.map(async (m) => ({ ...m, sync: await bumpSyncMeta(m, origin) }) as Message),
        );
        await this.db.messages.bulkPut(next);
      },
    );
  }

  async deleteMessage(id: string): Promise<void> {
    const existing = await this.db.messages.get(id);
    if (!existing) return;
    await this.db.messages.put({
      ...existing,
      sync: tombstone(existing.sync, getDeviceOrigin()),
    });
  }

  async getMessagePage(page: MessagePage): Promise<Message[]> {
    const { chatId, beforeOrder, afterOrder, limit } = page;
    // Cursor-style paging over the compound [chatId+order] index (#9), so a
    // long chat never loads whole into 6s+ memory.
    let collection;
    if (beforeOrder !== undefined) {
      collection = this.db.messages
        .where("[chatId+order]")
        .between([chatId, MIN_KEY], [chatId, beforeOrder], true, false)
        .reverse();
    } else if (afterOrder !== undefined) {
      collection = this.db.messages
        .where("[chatId+order]")
        .between([chatId, afterOrder], [chatId, MAX_KEY], false, true);
    } else {
      // Newest page by default (tail of the conversation).
      collection = this.db.messages
        .where("[chatId+order]")
        .between([chatId, MIN_KEY], [chatId, MAX_KEY], true, true)
        .reverse();
    }
    const rows = await collection.limit(limit).toArray();
    const live = rows.filter((m) => !m.sync.deletedAt);
    return live.sort((a, b) => a.order - b.order);
  }

  async countMessages(chatId: string): Promise<number> {
    return this.db.messages
      .where("chatId")
      .equals(chatId)
      .filter((m) => !m.sync.deletedAt)
      .count();
  }

  // ---- Asset bytes: separate table, content-addressed (decision #18) ----

  async getAssetBlob(hash: string): Promise<AssetBlob | undefined> {
    return this.db.assetBlobs.get(hash);
  }

  async putAssetBlob(blob: AssetBlob): Promise<void> {
    await measurePerformanceAsync(
      "write_cache",
      {
        inputSize: blob.data.size,
        fileId: "assetBlobs",
        packageId: blob.hash,
        cache: "miss",
        thread: "main",
        invalidationReason: "asset_blob_put",
      },
      () => this.db.assetBlobs.put(blob),
    );
  }

  async deleteAssetBlobIfOrphan(hash: string): Promise<boolean> {
    const refs = await this.db.assets
      .where("hash")
      .equals(hash)
      .filter((a) => !a.sync.deletedAt)
      .count();
    if (refs === 0) {
      await this.db.assetBlobs.delete(hash);
      return true;
    }
    return false;
  }

  // ---- Settings singleton ----

  async getSettings(): Promise<Settings | undefined> {
    return this.db.settings.get("global");
  }

  async putSettings(settings: Settings): Promise<void> {
    await measurePerformanceAsync(
      "save_project_state",
      {
        inputSize: estimateJsonInputSize(settings),
        fileId: "settings",
        packageId: settings.id,
        cache: "miss",
        thread: "main",
        invalidationReason: "settings_put",
      },
      () => this.db.settings.put(settings),
    );
  }

  // ---- Reactivity (decision #13) ----

  liveChats(): LiveQuery<Chat[]> {
    return toLiveQuery(async () => {
      const rows = await this.db.chats.toArray();
      return rows
        .filter((c) => !c.sync.deletedAt && !c.archived)
        .sort((a, b) => {
          // 고정 대화를 맨 위로(결정 #16 조직화), 그다음 최근 활동 순.
          if (!!a.pinned !== !!b.pinned) return a.pinned ? -1 : 1;
          return (b.lastMessageAt ?? 0) - (a.lastMessageAt ?? 0);
        });
    });
  }

  liveMessages(chatId: string): LiveQuery<Message[]> {
    return toLiveQuery(async () => {
      const rows = await this.db.messages.where("chatId").equals(chatId).toArray();
      return rows.filter((m) => !m.sync.deletedAt).sort((a, b) => a.order - b.order);
    });
  }

  liveCharacters(): LiveQuery<Character[]> {
    return toLiveQuery(async () => {
      const rows = await this.db.characters.toArray();
      return rows.filter((c) => !c.sync.deletedAt).sort((a, b) => a.name.localeCompare(b.name));
    });
  }

  liveGeneratedImages(filter?: {
    chatId?: string;
    characterId?: string;
    provider?: string;
  }): LiveQuery<GeneratedImage[]> {
    return toLiveQuery(async () => {
      let rows = await this.db.generatedImages.toArray();
      rows = rows.filter((g) => !g.sync.deletedAt);
      if (filter?.chatId) rows = rows.filter((g) => g.chatId === filter.chatId);
      if (filter?.characterId) rows = rows.filter((g) => g.characterId === filter.characterId);
      if (filter?.provider) rows = rows.filter((g) => g.provider === filter.provider);
      return rows.sort((a, b) => b.generatedAt - a.generatedAt);
    });
  }

  liveGameCards(): LiveQuery<GameCard[]> {
    return toLiveQuery(async () => {
      const rows = await this.db.gameCards.toArray();
      return this.gameCardRepository.normalizeRows(rows, { repair: false });
    });
  }

  liveInstalledRqtPacks(): LiveQuery<InstalledRqtPack[]> {
    return toLiveQuery(async () => {
      const rows = await this.db.installedRqtPacks.toArray();
      return rows
        .filter((pack) => !pack.sync.deletedAt)
        .sort((a, b) => a.name.localeCompare(b.name));
    });
  }

  liveSettings(): LiveQuery<Settings | undefined> {
    return toLiveQuery(async () => this.db.settings.get("global"));
  }

  // ---- Bulk export/import for backup (decision #6 interchange) ----

  async exportAll(): Promise<unknown> {
    const [
      characters,
      personas,
      chats,
      lorebooks,
      presets,
      scripts,
      providerProfiles,
      generatedImages,
      assets,
      messages,
      settings,
      gameCards,
      installedRqtPacks,
    ] = await Promise.all([
      this.db.characters.toArray(),
      this.db.personas.toArray(),
      this.db.chats.toArray(),
      this.db.lorebooks.toArray(),
      this.db.presets.toArray(),
      this.db.scripts.toArray(),
      this.db.providerProfiles.toArray(),
      this.db.generatedImages.toArray(),
      this.db.assets.toArray(),
      this.db.messages.toArray(),
      this.db.settings.toArray(),
      this.db.gameCards.toArray(),
      this.db.installedRqtPacks.toArray(),
    ]);
    // Asset blob bytes are exported separately by the asset pipeline
    // (decision #18); this is the metadata/entity snapshot.
    return {
      version: 1,
      characters,
      personas,
      chats,
      lorebooks,
      presets,
      scripts,
      providerProfiles,
      generatedImages,
      assets,
      messages,
      settings,
      gameCards,
      installedRqtPacks,
    };
  }

  async importAll(data: unknown): Promise<void> {
    const d = validateBackupImportData(data);
    await this.db.transaction(
      "rw",
      [
        this.db.characters,
        this.db.personas,
        this.db.chats,
        this.db.lorebooks,
        this.db.presets,
        this.db.scripts,
        this.db.providerProfiles,
        this.db.generatedImages,
        this.db.assets,
        this.db.messages,
        this.db.settings,
        this.db.gameCards,
        this.db.installedRqtPacks,
      ],
      async () => {
        if (d.characters) await this.db.characters.bulkPut(d.characters as Character[]);
        if (d.personas) await this.db.personas.bulkPut(d.personas as Persona[]);
        if (d.chats) await this.db.chats.bulkPut(d.chats as Chat[]);
        if (d.lorebooks) await this.db.lorebooks.bulkPut(d.lorebooks as Lorebook[]);
        if (d.presets) await this.db.presets.bulkPut(d.presets as Preset[]);
        if (d.scripts) await this.db.scripts.bulkPut(d.scripts as Script[]);
        if (d.providerProfiles)
          await this.db.providerProfiles.bulkPut(d.providerProfiles as ProviderProfile[]);
        if (d.generatedImages)
          await this.db.generatedImages.bulkPut(d.generatedImages as GeneratedImage[]);
        if (d.assets) await this.db.assets.bulkPut(d.assets as Asset[]);
        if (d.messages) await this.db.messages.bulkPut(d.messages as Message[]);
        if (d.settings) await this.db.settings.bulkPut(d.settings as Settings[]);
        if (d.gameCards) await this.db.gameCards.bulkPut(d.gameCards as GameCard[]);
        if (d.installedRqtPacks)
          await this.db.installedRqtPacks.bulkPut(d.installedRqtPacks as InstalledRqtPack[]);
      },
    );
  }
}
